<script setup lang="ts">
import { computed, ref } from 'vue'
import { Download, X } from '@lucide/vue'
import type { ParsedDocument } from '../app/types'
import { getExportOptions } from '../exporters/options'
import StatusNotice from './StatusNotice.vue'

const props = defineProps<{
  document: ParsedDocument
  filteredDocument?: ParsedDocument
  filteredRowCount?: number
}>()

const emit = defineEmits<{
  close: []
  done: [message: string]
  error: [message: string]
}>()

const useFiltered = ref(Boolean(props.filteredDocument))
const activeDocument = computed(() => useFiltered.value && props.filteredDocument
  ? props.filteredDocument
  : props.document)
const options = computed(() => getExportOptions(activeDocument.value))
const selected = ref(options.value[0]?.id ?? 'txt')
const exporting = ref(false)

async function runExport() {
  const option = options.value.find((item) => item.id === selected.value)
  if (!option) {
    return
  }

  exporting.value = true
  try {
    const { exportDocument } = await import('../exporters/exportDocument')
    await exportDocument(activeDocument.value, option)
    emit('done', useFiltered.value ? '已导出当前筛选结果，原文件不会被覆盖。' : '已生成导出副本，原文件不会被覆盖。')
  } catch (error) {
    emit('error', error instanceof Error ? error.message : '导出失败')
  } finally {
    exporting.value = false
  }
}
</script>

<template>
  <div class="sheet-backdrop" role="presentation" @click.self="emit('close')">
    <section class="bottom-sheet" role="dialog" aria-modal="true" aria-labelledby="export-title">
      <header class="sheet-head">
        <div>
          <p class="eyebrow">导出副本</p>
          <h2 id="export-title">选择格式与兼容性</h2>
        </div>
        <button class="icon-button" type="button" aria-label="关闭导出面板" @click="emit('close')">
          <X :size="20" aria-hidden="true" />
        </button>
      </header>

      <label v-if="filteredDocument" class="export-filter-toggle">
        <input v-model="useFiltered" type="checkbox" />
        <span>
          <strong>仅导出当前筛选结果</strong>
          <small>{{ filteredRowCount ?? 0 }} 行，按当前排序与可见列导出</small>
        </span>
      </label>

      <div class="export-options">
        <label v-for="option in options" :key="option.id" class="export-option">
          <input v-model="selected" type="radio" name="export-format" :value="option.id" />
          <span>
            <strong>{{ option.label }}</strong>
            <small>.{{ option.extension }}</small>
          </span>
        </label>
      </div>

      <StatusNotice
        v-if="options.find((option) => option.id === selected)?.warning"
        tone="warning"
      >
        {{ options.find((option) => option.id === selected)?.warning }}
      </StatusNotice>

      <button class="primary-action full" type="button" :disabled="exporting" @click="runExport">
        <Download :size="20" aria-hidden="true" />
        {{ exporting ? '正在导出' : '导出副本' }}
      </button>
    </section>
  </div>
</template>
